import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { footerStyle } from '@components/footer/config/footer-style';
import { Footer, FooterStyle } from '@components/footer/model';
import { Navigation } from '@components/navigation/model';
import { environment } from '@environments/environment';
import { CommonService } from '@services/common/common.service';
import { CommonMenu } from '@services/common/model';
import { ResponsiveService } from '@services/responsive';
import { SeoService } from '@services/seo';
import { JsonLD } from '@services/seo/model';
import { SocialNetworksComponent } from '@shared/components/social-networks';
import { socialNetworksStyle } from '@shared/components/social-networks/config';
import { SocialNetworksStyle } from '@shared/components/social-networks/model';
import { RoutesPath } from '@shared/constants';
import { DynamicUrlDirective } from '@shared/directives';
import { ColorPipe } from '@shared/pipes';
import { resolveAbsoluteUrl } from '@shared/utils';
import { map, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-footer',
  imports: [RouterLink, SocialNetworksComponent, AsyncPipe, NgOptimizedImage, ColorPipe, DynamicUrlDirective],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent {
  readonly data = input.required<CommonMenu<Navigation | Footer>[]>();
  breakpoints = inject(ResponsiveService).breakpoints;
  protected readonly routeToHomepage = RoutesPath.HOME;
  style = signal<FooterStyle>(null!);
  socialNetworksStyle = signal<SocialNetworksStyle>(null!);
  dataFooter$: Observable<Footer> = inject(CommonService)
    .getFooter$()
    .pipe(
      tap((menu) => {
        this.style.set(footerStyle[menu.theme || 'BLUE']!);
        this.socialNetworksStyle.set(socialNetworksStyle[this.style().socialNetworksStyle]!);
      }),
      map((menu) => this.data().find((data) => data.id === menu.id)),
      map((footer) => footer?.data as Footer),
      tap((footer) => {
        this.#setJsonLd(footer);
      })
    );
  readonly #seoService = inject(SeoService);

  #setJsonLd(data: Footer) {
    const jsonLd = this.#buildSiteNavigationJsonLd(data);
    this.#seoService.addJsonLdScript('navigation-secondaire', jsonLd);
  }

  #buildSiteNavigationJsonLd(data: Footer): JsonLD {
    const nav = new Set(
      [...data.links.primary, ...data.links.secondary].filter((item) => !/^https?:\/\//.test(item.url))
    );
    const hasPart = Array.from(nav).map((item) => ({
      '@type': 'SiteNavigationElement',
      name: item.label,
      url: resolveAbsoluteUrl(item.url),
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Navigation secondaire',
      url: environment.hostname,
      hasPart,
    };
  }
}
